import React, { Dispatch, Fragment, SetStateAction } from 'react'
import { formatDate, getValueFromObject } from '@helpers/utils'
import Translations from '@helpers/translations'
import { getAnalyticsCountryCode } from '@helpers/env'
import BookCustomerServiceDismissible from '@components/ui/BookCustomerService/BookCustomerServiceDismissible'
import { returnsClickEvent } from '@components/returns/helpers'
import ProductCard from '@components/ui/ProductCard'
import ProductCardFooter, {
  ProductCardFooterProps
} from '@components/returns/ItemsSelection/ReturnsContent/ProductCardFooter'
import QRCodeBox from './QRCodeBox'
import styles from './styles.module.scss'
import { Returns, ReturnsProduct } from '@api/returns/types'
import { NotificationProps } from '@components/ui/Notification'
import { EmptyObject } from 'types/helpers'
import { DEFAULT_IMAGE } from '@components/ui/Modal/ModalProductCard'

type ReturnProductCardProps = {
  product: ReturnsProduct
  setProductToReturn: ProductCardFooterProps['setProductToReturn']
  cancelProductToReturn: ProductCardFooterProps['cancelProductToReturn']
  processed: boolean
  isExpired: boolean
}

function ReturnProductCard({
  product,
  processed = false,
  isExpired = false,
  setProductToReturn,
  cancelProductToReturn
}: ReturnProductCardProps) {
  const productProperties = {
    color: product.color,
    ...product.sizes
  }
  const newProductPropertiesToCompare = {
    color: getValueFromObject(product, 'return.color.title', undefined),
    ...product.return?.sizes
  }
  const productImage =
    getValueFromObject(product, 'return.color.image.src', undefined) || product.image || DEFAULT_IMAGE

  return (
    <div
      className={`${styles.returns__container} ${styles.returns__section__productCard}`}
      data-testid={`product-card-${product.title}`}
    >
      <ProductCard
        title={product.product_type}
        image={productImage}
        properties={productProperties}
        newPropertiesToCompare={newProductPropertiesToCompare}
        price={{
          paidPrice: product.paid_price,
          fullPrice: product.full_price
        }}
      >
        <ProductCardFooter
          product={{
            productId: product.id,
            productType: product.product_type,
            isReturnable: product.isReturnable,
            isExchangeable: product.isExchangeable,
            isMTO: product.isMTO
          }}
          isExpired={isExpired}
          isReturnProcessed={processed}
          returnMethod={product.return?.method}
          setProductToReturn={setProductToReturn}
          cancelProductToReturn={cancelProductToReturn}
        />
      </ProductCard>
    </div>
  )
}

type OnError = Dispatch<
  SetStateAction<
    | {
        type: NotificationProps['type']
        content: NotificationProps['content']
      }
    | EmptyObject
  >
>

type ReturnContentProps = {
  orderProducts: ReturnsProduct[]
  processedReturns?: Returns
  setProductToReturn: ProductCardFooterProps['setProductToReturn']
  cancelProductToReturn: ProductCardFooterProps['cancelProductToReturn']
  onError: OnError
  isExpired: boolean
}

export default function ReturnContent({
  orderProducts,
  processedReturns,
  setProductToReturn,
  cancelProductToReturn,
  isExpired
}: ReturnContentProps) {
  const emptyProducts = !orderProducts.length
  return (
    <div className={styles.returns}>
      {!emptyProducts && (
        <div className={styles.returns__section__content}>
          <BookCustomerServiceDismissible
            title="Doesn’t fit perfectly?"
            className={`${styles.returns__container} ${styles.returns__container_customerService}`}
            utmSource="returns"
            onButtonClick={() => {
              returnsClickEvent({
                label: 'Open fit tune up modal',
                store_location: getAnalyticsCountryCode(),
                url: window.location.href
              })
            }}
          >
            <p>Book a free call with one of our fit experts for a personalised size recommendation.</p>
            <br />
            <p>Let’s get fit right.</p>
          </BookCustomerServiceDismissible>
          {orderProducts.map((product, index) => (
            <ReturnProductCard
              key={`${product.id}-${index}`}
              product={product}
              setProductToReturn={setProductToReturn}
              cancelProductToReturn={cancelProductToReturn}
              isExpired={isExpired}
              processed={false}
            />
          ))}
        </div>
      )}

      {!!processedReturns && (
        <div className={styles.returns__section}>
          {Object.keys(processedReturns).map((returnId, index) => {
            const returnData = processedReturns[returnId]
            return (
              <Fragment key={returnId}>
                <div
                  className={`${styles.returns__section__separator} ${
                    emptyProducts && index === 0 ? styles.returns__section__separator_emptyProduct : ''
                  }`}
                  id={returnId}
                >
                  <div />
                </div>
                <div className={styles.returns__processedOrderTitle}>
                  <span className={styles.returns__processedDate}>
                    {returnData.label_url
                      ? `${Translations.localise('pages.returns.return-created')} ${formatDate(returnData.return_date)}`
                      : Translations.localise('pages.returns.finish-your-return')}
                  </span>
                  <span className={styles.returns__processedLength}>
                    {returnData.products.length} {Translations.localise('pages.returns.items-selected')}
                  </span>
                </div>
                <div className={styles.returns__section__content}>
                  {returnData.return_date && <QRCodeBox labelUrl={returnData.label_url} />}

                  {returnData.products.map(product => (
                    <ReturnProductCard
                      key={`${product.id}-${index}`}
                      product={product}
                      setProductToReturn={setProductToReturn}
                      cancelProductToReturn={cancelProductToReturn}
                      isExpired={isExpired}
                      processed
                    />
                  ))}
                </div>
              </Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}
